import { FetchStrategy } from "@betomorrow/micro-stores"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useMySpaceNavigate } from "@modules/navigation/useMySpaceNavigate"
import { ProgramInfoDetails } from "@modules/programs/components/programInfoDetails"
import { ProgramInfoHeader } from "@modules/programs/components/programInfoHeader"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOffStatus, ProgramOnOffTypeOf } from "@modules/programs/programOnOffTypes"
import { ProgramSubscribersList } from "@modules/programs/programSubscriber/components/programSubscribersList"
import { ProgramTypeOf, ProgramVisibility } from "@modules/programs/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useToast } from "@modules/ui/components/huToast"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { useEffect, useTransition } from "react"

type ProgramInfoResumeProps = {
  programId: string
  programType: ProgramTypeOf
  editionAllowed?: boolean
}

export const ProgramInfoResume: React.FC<ProgramInfoResumeProps> = ({
  programId,
  programType,
  editionAllowed = true,
}) => {
  const { setCurrentStackState, getCurrentStackState } = useSidebar()
  const [, startTransition] = useTransition()

  const activeIndex = getCurrentStackState("activeIndex") || 0
  const { result: program } = useProgram(programId, programType, FetchStrategy.Always)
  const { setCustomHeaderIcons } = useSidebar()
  const toast = useToast()
  const navigate = useMySpaceNavigate()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(program ?? undefined)

  function selectTab(index: number) {
    startTransition(() => {
      setCurrentStackState("activeIndex", index)
    })
  }

  useEffect(() => {
    const customHeaderIcons = []

    const canUserChatWithMembers =
      program &&
      program._programType !== ProgramOnOffTypeOf &&
      (isAuthor || isAcceptedSharedCoach || program.subscribed)

    if (canUserChatWithMembers) {
      const openChatWithProgramGroup = () => {
        if (programId) {
          navigate(Path.Chat.Root, { state: { programToChat: programId } })
        }
      }

      customHeaderIcons.push({
        onClick: openChatWithProgramGroup,
        iconView: <span className="pi pi-comments" />,
        tooltipOptions: {
          isTooltipDisabled: false,
          tooltipId: "chat-with-members",
          tooltipContent: word("chat.button.goToProgramChat"),
        },
      })
    }

    const canUserShareInvitationLink =
      program &&
      program.invitationLink &&
      (program._programType === ProgramInfiniteTypeOf || program.status === ProgramOnOffStatus.PUBLISHED) &&
      (program.visibility === ProgramVisibility.PUBLIC ||
        (program.visibility === ProgramVisibility.PRIVATE && (isAuthor || isAcceptedSharedCoach)))

    if (canUserShareInvitationLink) {
      customHeaderIcons.push({
        onClick: () => {
          navigator.clipboard.writeText(program.invitationLink)
          toast.show({ severity: "info", detail: word("program.details.share.success") })
        },
        iconView: <span className="pi pi-upload" />,
        tooltipOptions: {
          isTooltipDisabled: false,
          tooltipId: "dynamic-link",
          tooltipContent: word("sidebar.button.dynamicLink"),
        },
      })
    }

    if (customHeaderIcons.length > 0) {
      setCustomHeaderIcons(customHeaderIcons)
    }
  }, [isAuthor, isAcceptedSharedCoach, program?.invitationLink, program?.visibility, program?.subscribed])

  return (
    <>
      {program ? (
        <div className="flex flex-column flex-grow-1">
          <ProgramInfoHeader
            program={program}
            activeIndex={activeIndex}
            setActiveIndex={(value: number) => selectTab(value)}
          />
          {activeIndex === 0 && <ProgramInfoDetails program={program} editionAllowed={editionAllowed} />}
          {activeIndex === 1 && <ProgramSubscribersList program={program} />}
        </div>
      ) : (
        <LoadingSpinner />
      )}
    </>
  )
}
